// Catalog.ID Client — Account Blob Store API
// ============================================================================
// The HTTP half of the per-account opaque document store
// (cid-encrypted-storage §4.2). Kept OUT of `account.ts` because nothing here
// touches identity: a blob is application state whose loss costs a document,
// not a key, and the two have different lifetimes, different caps and different
// recovery stories (§3.4.9).
//
// WHY the typed errors. The store's whole protocol is compare-and-set, and the
// server distinguishes its three refusals only by HTTP status plus an `error`
// string: 409 means "someone else wrote, here is the generation you must
// re-read against", 404 means "no such row", 413 means "you are over a cap".
// A caller that reads `err.status` at every call site gets one of those wrong
// eventually — a 409 mistaken for a failure loses a user's edit. So the three
// become classes, the conflict carries the server's `generation`, and every
// other status passes through as the original `ApiError`.
//
// The route is TWO segments (`{moduleId}/{name}`), not one escaped key, so a
// module id can never be smuggled through a name. The caller does not choose
// the module id on the RPC path: `key-grant/blob-handlers.ts` applies the
// hub-attested sender as the prefix (§4.3).

import { api, ApiError } from './client';
import type {
    BlobManifestEntry, BlobRecord, PutBlobBody, PutBlobResult,
} from './types';

export type { BlobManifestEntry, BlobRecord, PutBlobBody, PutBlobResult };

/**
 * The compare-and-set lost. `generation` is what the server holds RIGHT NOW —
 * 0 when the row does not exist — so a caller re-reads at that generation,
 * re-applies its edit and writes again. Never a reason to give up on the edit.
 */
export class BlobConflictError extends Error {
    constructor(public readonly generation: number) {
        super(`blob generation mismatch (server holds generation ${generation})`);
        this.name = 'BlobConflictError';
    }
}

/** No row under this key. A read or delete of a blob that was never written. */
export class BlobNotFoundError extends Error {
    constructor(public readonly blobKey: string) {
        super(`no blob at '${blobKey}'`);
        this.name = 'BlobNotFoundError';
    }
}

/**
 * A cap refused the write: the single-blob ceiling (`blob_too_large`) or one of
 * the per-account ceilings (`blob_quota_exceeded`). Which one is the server's
 * `error` string, kept verbatim so the caller can say something true.
 */
export class BlobQuotaError extends Error {
    constructor(public readonly serverError: string) {
        super(`blob write refused: ${serverError}`);
        this.name = 'BlobQuotaError';
    }
}

function blobPath(moduleId: string, name: string): string {
    return `/api/account/blobs/${moduleId}/${name}`;
}

function serverError(err: ApiError): string {
    const body = err.body as { error?: unknown } | null;
    return typeof body === 'object' && body !== null && typeof body.error === 'string'
        ? body.error
        : `http_${err.status}`;
}

function serverGeneration(err: ApiError): number {
    const body = err.body as { generation?: unknown } | null;
    return typeof body === 'object' && body !== null && typeof body.generation === 'number'
        ? body.generation
        : 0;
}

/** The account-wide manifest: every namespace, metadata only. */
export async function listBlobs(): Promise<{ blobs: BlobManifestEntry[] }> {
    return api.get<{ blobs: BlobManifestEntry[] }>('/api/account/blobs');
}

/** @throws {BlobNotFoundError} when the account holds no such blob. */
export async function getBlob(moduleId: string, name: string): Promise<BlobRecord> {
    try {
        return await api.get<BlobRecord>(blobPath(moduleId, name));
    } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
            throw new BlobNotFoundError(`${moduleId}/${name}`);
        }
        throw err;
    }
}

/**
 * Compare-and-set write. `ifGeneration` is the generation this write expects to
 * replace; 0 creates.
 *
 * @throws {BlobConflictError} when the stored generation differs — nothing was written.
 * @throws {BlobQuotaError} when a size or count cap refused it.
 */
export async function putBlob(
    moduleId: string,
    name: string,
    body: PutBlobBody,
    ifGeneration: number,
): Promise<PutBlobResult> {
    try {
        return await api.put<PutBlobResult>(blobPath(moduleId, name), body, {
            headers: { 'If-Match': String(ifGeneration) },
        });
    } catch (err) {
        if (err instanceof ApiError) {
            if (err.status === 409) throw new BlobConflictError(serverGeneration(err));
            if (err.status === 413) throw new BlobQuotaError(serverError(err));
            if (err.status === 404) throw new BlobNotFoundError(`${moduleId}/${name}`);
        }
        throw err;
    }
}

/** @throws {BlobNotFoundError} when the account holds no such blob. */
export async function deleteBlob(moduleId: string, name: string): Promise<void> {
    try {
        await api.del(blobPath(moduleId, name));
    } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
            throw new BlobNotFoundError(`${moduleId}/${name}`);
        }
        throw err;
    }
}
